import { generateClient } from 'aws-amplify/api';
import { MDBBtn, MDBCard, MDBCardBody, MDBCardTitle, MDBIcon, MDBRow, MDBCol, MDBTable, MDBTableBody, MDBTableHead, MDBSpinner, MDBAlert, MDBModal, MDBModalDialog, MDBModalContent, MDBModalBody } from "mdb-react-ui-kit";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../App";
import { getLocalizedText } from "../../utils/localizedText";
import { isoToGermanDate } from "../../utils/dateTools";
import { captureError } from '../../utils/sentryHelpers';
import LectureRegistration from "../../components-user/event-management/lecture-registration/Index";
import lounges26LectureConfig from "../event-management/lectures/lectureEventConfig";

const client = generateClient();

export default function MyLectures() {
    const { t, i18n } = useTranslation();
    const { user } = useContext(UserContext);

    const [lectures, setLectures] = useState([]);
    const [isLoadingLectures, setIsLoadingLectures] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedLectureForEdit, setSelectedLectureForEdit] = useState(null);
    const [selectedEventForLecture, setSelectedEventForLecture] = useState(null);

    // Fetch user's lectures
    const fetchMyLectures = async () => {
        setIsLoadingLectures(true);
        setError(null);
        try {
            const userId = user?.id || user?.attributes?.["custom:userId"];

            if (!userId) {
                console.error("No user ID found");
                setIsLoadingLectures(false);
                return;
            }

            const response = await client.graphql({
                query: /* GraphQL */ `
                    query GetUser($id: ID!) {
                        getUser(id: $id) {
                            id
                            owner
                            lectures {
                                items {
                                    lecture {
                                        id
                                        createdAt
                                        approved
                                        title {
                                            language
                                            text
                                        }
                                        content {
                                            language
                                            text
                                        }
                                        speakers {
                                            items {
                                                id
                                                employee {
                                                    image {
                                                        fileName
                                                        alt
                                                    }
                                                    user {
                                                        firstName
                                                        lastName
                                                    }
                                                }
                                            }
                                        }
                                        speakersUsers {
                                            items {
                                                id
                                                user {
                                                    id
                                                    firstName
                                                    lastName
                                                    image {
                                                        fileName
                                                        alt
                                                    }
                                                }
                                            }
                                        }
                                        company {
                                            id
                                            name
                                            image {
                                                main {
                                                    fileName
                                                    alt
                                                }
                                            }
                                        }
                                        topics {
                                            items {
                                                topic {
                                                    name {
                                                        text
                                                        language
                                                    }
                                                }
                                            }
                                        }
                                        type
                                        companyBooth
                                        room
                                        startTime
                                        endTime
                                        event {
                                            id
                                            type
                                            name {
                                                language
                                                text
                                            }
                                            image {
                                                fileName
                                            }
                                            lectureEditEndDate
                                            startDate
                                        }
                                        image {
                                            fileName
                                        }
                                        pdfDe {
                                            fileName
                                            s3Path
                                        }
                                        pdfEn {
                                            fileName
                                            s3Path
                                        }
                                        videoS3Key {
                                            s3Path
                                        }
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: { id: userId }
            });

            const lectureItems = response?.data?.getUser?.lectures?.items || [];
            const extractedLectures = lectureItems
                .map(item => item.lecture)
                .filter(Boolean); // Remove any null/undefined lectures

            // Sort by event start date (most recent first), then by start time
            const sortedLectures = extractedLectures.sort((a, b) => {
                // Sort by event date first (most recent event first)
                const eventDateA = a.event?.startDate || '';
                const eventDateB = b.event?.startDate || '';
                const eventComparison = eventDateB.localeCompare(eventDateA);
                if (eventComparison !== 0) return eventComparison;

                // Same event, sort by lecture start time
                if (!a.startTime && !b.startTime) return 0;
                if (!a.startTime) return 1;
                if (!b.startTime) return -1;
                return a.startTime.localeCompare(b.startTime);
            });

            setLectures(sortedLectures);
        } catch (error) {
            console.error("Error fetching my lectures:", error);
            setError(error.message || "Failed to load lectures");
            captureError(error, {
                feature: 'my-lectures',
                operation: 'fetch-my-lectures',
                context: { userId: user?.id }
            });
        }
        setIsLoadingLectures(false);
    };

    useEffect(() => {
        if (user) {
            fetchMyLectures();
        }
    }, [user]);

    // Check if user can edit a lecture based on event's lectureEditEndDate
    const canEditLecture = (lecture) => {
        if (!lecture?.event) return false;

        // Check if edit period is still valid
        const today = new Date().toISOString().split('T')[0];
        const editEndDate = lecture.event.lectureEditEndDate;

        if (editEndDate && today > editEndDate) {
            return false;
        }

        return true;
    };

    const handleEditLecture = (lecture) => {
        setSelectedLectureForEdit(lecture.id);
        setSelectedEventForLecture(lecture.event);
        setShowEditModal(true);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setSelectedLectureForEdit(null);
        setSelectedEventForLecture(null);
    };

    const handleSaveLecture = (lectureData) => {
        console.log('Lecture saved:', lectureData);

        // Update the lecture in the local state
        setLectures(prevLectures =>
            prevLectures.map(lecture =>
                lecture.id === lectureData.id ? lectureData : lecture
            )
        );

        // Close modal
        handleCloseModal();

        // Refresh lectures to get latest data
        fetchMyLectures();
    };

    // Show loading state
    if (isLoadingLectures) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                <MDBSpinner style={{ width: '3rem', height: '3rem' }} className="me-3">
                    <span className="visually-hidden">{t("Loading...")}</span>
                </MDBSpinner>
                <span>{t('Loading your lectures...')}</span>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <MDBAlert color="danger" className="m-4">
                <h4 className="alert-heading">{t('Error Loading Data')}</h4>
                <p>{error}</p>
                <hr />
                <p className="mb-0">
                    <MDBBtn color="danger" outline onClick={fetchMyLectures}>
                        {t('Try Again')}
                    </MDBBtn>
                </p>
            </MDBAlert>
        );
    }

    return (
        <div style={{ maxWidth: "1500px" }} className="mx-auto my-5">
            {/* Header Card */}
            <MDBCard className="mb-4">
                <MDBCardBody className="p-4">
                    <MDBRow>
                        <MDBCol md='12'>
                            <MDBCardTitle>
                                <h2 className="mb-0">
                                    <MDBIcon icon="user-graduate" className="fas me-3" />
                                    {t("My Lectures")}
                                </h2>
                            </MDBCardTitle>
                            <p className="text-muted mt-2 mb-0">
                                {t("Here you can see all lectures where you are registered as a speaker.")}
                            </p>
                        </MDBCol>
                    </MDBRow>
                </MDBCardBody>
            </MDBCard>

            {/* Lectures Table */}
            <MDBCard>
                <MDBCardBody>
                    <MDBTable hover responsive>
                        <MDBTableHead>
                            <tr>
                                <th>{t("Event")}</th>
                                <th>{t("Title")}</th>
                                <th>{t("Type")}</th>
                                <th>{t("Speakers")}</th>
                                <th>{t("Date, Time & Room")}</th>
                                <th>{t("Company")}</th>
                                <th>{t("Actions")}</th>
                            </tr>
                        </MDBTableHead>
                        <MDBTableBody>
                            {lectures.length > 0 ? (
                                lectures.map((lecture) => (
                                    <tr key={lecture.id}>
                                        <td>
                                            <small className="text-muted">
                                                {lecture.event?.name && (
                                                    lecture.event.name.find(n => n.language === i18n.language)?.text ||
                                                    lecture.event.name[0]?.text
                                                )}
                                            </small>
                                        </td>
                                        <td>
                                            <strong>
                                                {lecture.title?.find(t => t.language === i18n.language)?.text ||
                                                 lecture.title?.[0]?.text}
                                            </strong>
                                        </td>
                                        <td>
                                            <span className={`badge ${
                                                lecture.type === 'vip' ? 'bg-primary' :
                                                lecture.type === 'technical' ? 'bg-info' :
                                                lecture.type === 'educational' ? 'bg-success' :
                                                lecture.type === 'marketing' ? 'bg-warning' :
                                                'bg-secondary'
                                            }`}>
                                                {t(lecture.type)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {/* Show employee speakers */}
                                                {lecture.speakers?.items?.slice(0, 2).map((speaker, index) => (
                                                    <div key={index} className="me-1">
                                                        {speaker?.employee?.image?.fileName ? (
                                                            <img
                                                                src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speaker.employee.image.fileName}`}
                                                                className="rounded-circle border"
                                                                style={{
                                                                    height: "30px",
                                                                    width: "30px",
                                                                    objectFit: "cover",
                                                                }}
                                                                alt={speaker.employee?.image.alt}
                                                                title={`${speaker.employee?.user?.firstName} ${speaker.employee?.user?.lastName}`}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle border bg-light d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    height: "30px",
                                                                    width: "30px",
                                                                }}
                                                            >
                                                                <MDBIcon fas icon="user" size="sm" />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                                {/* Show direct user speakers */}
                                                {lecture.speakersUsers?.items?.slice(0, 2).map((speakerUser, index) => (
                                                    <div key={`user-${index}`} className="me-1">
                                                        {speakerUser?.user?.image?.fileName ? (
                                                            <img
                                                                src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speakerUser.user.image.fileName}`}
                                                                className="rounded-circle border"
                                                                style={{
                                                                    height: "30px",
                                                                    width: "30px",
                                                                    objectFit: "cover",
                                                                }}
                                                                alt={speakerUser.user?.image?.alt}
                                                                title={`${speakerUser.user?.firstName} ${speakerUser.user?.lastName}`}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle border bg-light d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    height: "30px",
                                                                    width: "30px",
                                                                }}
                                                            >
                                                                <MDBIcon fas icon="user" size="sm" />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                                {(lecture.speakers?.items?.length + (lecture.speakersUsers?.items?.length || 0)) > 2 && (
                                                    <small className="text-muted">
                                                        +{(lecture.speakers?.items?.length || 0) + (lecture.speakersUsers?.items?.length || 0) - 2}
                                                    </small>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {lecture.startTime ? (
                                                <div>
                                                    <div><strong>{isoToGermanDate(lecture.startTime)}</strong></div>
                                                    <small className="text-muted">
                                                        {lecture.startTime.substring(11, 16)} - {lecture.endTime?.substring(11, 16)}
                                                    </small>
                                                    {lecture.room && (
                                                        <div>
                                                            <small className="text-muted">{t("Room")}: {lecture.room}</small>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted">{t("Not scheduled yet")}</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {lecture.company?.image?.main?.fileName && (
                                                    <img
                                                        src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${lecture.company.image.main.fileName}`}
                                                        alt={lecture.company.name}
                                                        className="me-2"
                                                        style={{ height: "24px", width: "24px", objectFit: "contain" }}
                                                    />
                                                )}
                                                <small>{lecture.company?.name}</small>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column gap-1">
                                                {canEditLecture(lecture) ? (
                                                    <MDBBtn
                                                        color='primary'
                                                        size="sm"
                                                        onClick={() => handleEditLecture(lecture)}
                                                    >
                                                        <MDBIcon fas icon="edit" className="me-1" />
                                                        {t("Edit")}
                                                    </MDBBtn>
                                                ) : (
                                                    <MDBBtn
                                                        color='secondary'
                                                        size="sm"
                                                        disabled
                                                        title={t("Edit period has ended")}
                                                    >
                                                        <MDBIcon fas icon="lock" className="me-1" />
                                                        {t("Locked")}
                                                    </MDBBtn>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5">
                                        <MDBIcon fas icon="chalkboard-teacher" size="3x" className="text-muted mb-3" />
                                        <h5 className="text-muted">{t("No lectures found")}</h5>
                                        <p className="text-muted">
                                            {t("You are not registered as a speaker for any lectures yet.")}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </MDBTableBody>
                    </MDBTable>
                </MDBCardBody>
            </MDBCard>

            {/* Edit Lecture Modal */}
            <MDBModal open={showEditModal} onClose={() => {}} staticBackdrop>
                <MDBModalDialog size="fullscreen">
                    <MDBModalContent>
                        <MDBModalBody className="p-0">
                            <LectureRegistration
                                companyId={lectures.find(l => l.id === selectedLectureForEdit)?.company?.id}
                                eventId={selectedEventForLecture?.id}
                                lectureId={selectedLectureForEdit}
                                eventConfig={lounges26LectureConfig}
                                onSaveLecture={handleSaveLecture}
                                onCloseRequest={handleCloseModal}
                                isModal={true}
                            />
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </div>
    );
}
